/**
 * VEDANGJYOTISHAM V6.0 — COMMON QUESTION EVALUATION HELPERS
 * 
 * Reusable Jyotish calculation and timing utilities shared across all 13 Life Domain catalogues.
 */

import { CompleteKundali, PlanetName } from '../../../types/kundali';
import { QuestionTimingWindow } from '../../../types/lifeDomainQuestions';
import { getSavByBhava } from '../../vedicEngine';

export function getHouseBindus(kundali: CompleteKundali, houseNum: number): number {
  if (kundali.ashtakvarga) {
    return getSavByBhava(kundali.ashtakvarga, houseNum, kundali.houses);
  }
  return 28 + ((houseNum * 3) % 7);
}

export function getPlanetHouse(kundali: CompleteKundali, name: string): number {
  const p = kundali.planets?.find(pl => pl.name.toLowerCase() === name.toLowerCase());
  return p ? p.house : 1;
}

export function getActiveDasha(kundali: CompleteKundali, focusDate?: Date | string): { maha: PlanetName; antar: PlanetName; pratyantar: PlanetName } {
  if (kundali.dashas && kundali.dashas.length > 0) {
    const refMs = focusDate ? new Date(focusDate).getTime() : new Date().getTime();
    const currentMd = kundali.dashas.find((d, idx) => {
      const s = new Date(d.startDate).getTime();
      const e = new Date(d.endDate).getTime();
      const isLast = idx === kundali.dashas!.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || kundali.dashas.find(d => d.isCurrent) || kundali.dashas[0];

    const antars = currentMd.antardashas || [];
    const currentAd = antars.find((a, idx) => {
      const s = new Date(a.startDate).getTime();
      const e = new Date(a.endDate).getTime();
      const isLast = idx === antars.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || antars.find(a => a.isCurrent) || antars[0];

    const pratyantars = currentAd?.pratyantardashas || [];
    const currentPd = pratyantars.find((p, idx) => {
      const s = new Date(p.startDate).getTime();
      const e = new Date(p.endDate).getTime();
      const isLast = idx === pratyantars.length - 1;
      return refMs >= s && (refMs < e || (isLast && refMs <= e));
    }) || pratyantars.find(p => p.isCurrent) || pratyantars[0];

    return {
      maha: (currentMd.planet || 'Jupiter') as PlanetName,
      antar: (currentAd?.planet || 'Saturn') as PlanetName,
      pratyantar: (currentPd?.planet || currentAd?.planet || 'Mercury') as PlanetName
    };
  }
  return { maha: 'Jupiter', antar: 'Saturn', pratyantar: 'Mercury' };
}

export function computeDynamicTimingWindow(
  kundali: CompleteKundali,
  targetHouse: number,
  rulingPlanets: PlanetName[],
  options?: { focusDate?: Date | string }
): QuestionTimingWindow {
  const focus = options?.focusDate ? new Date(options.focusDate) : new Date();

  // Find next auspicious dasha period matching ruling planets
  let startDate = new Date(focus.getTime() + 45 * 86400000);
  let endDate = new Date(focus.getTime() + 270 * 86400000);
  let isTimingActiveNow = false;

  if (kundali.dashas && kundali.dashas.length > 0) {
    for (const md of kundali.dashas) {
      if (!md.antardashas) continue;
      for (const ad of md.antardashas) {
        const adStart = new Date(ad.startDate);
        const adEnd = new Date(ad.endDate);
        const adLord = ad.planet as PlanetName;
        const mdLord = md.planet as PlanetName;

        if (rulingPlanets.includes(adLord) || rulingPlanets.includes(mdLord)) {
          // If active now
          if (focus.getTime() >= adStart.getTime() && focus.getTime() <= adEnd.getTime()) {
            startDate = adStart;
            endDate = adEnd;
            isTimingActiveNow = true;
            break;
          }
          // If future upcoming
          if (adStart.getTime() > focus.getTime()) {
            startDate = adStart;
            endDate = adEnd;
            isTimingActiveNow = false;
            break;
          }
        }
      }
      if (isTimingActiveNow) break;
    }
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const startStr = `${startDate.getDate().toString().padStart(2, '0')} ${months[startDate.getMonth()]} ${startDate.getFullYear()}`;
  const endStr = `${endDate.getDate().toString().padStart(2, '0')} ${months[endDate.getMonth()]} ${endDate.getFullYear()}`;
  
  const midTime = (startDate.getTime() + endDate.getTime()) / 2;
  const peakDateObj = new Date(midTime);
  const peakStr = `${peakDateObj.getDate().toString().padStart(2, '0')} ${months[peakDateObj.getMonth()]} ${peakDateObj.getFullYear()}`;

  const durationDays = Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / 86400000));
  const weeks = Math.round(durationDays / 7);
  const durationLabelEn = durationDays > 90 ? `~${Math.round(durationDays / 30.4)} months (${durationDays} days)` : `~${weeks} weeks (${durationDays} days)`;
  const durationLabelHi = durationDays > 90 ? `~${Math.round(durationDays / 30.4)} माह (${durationDays} दिन)` : `~${weeks} सप्ताह (${durationDays} दिन)`;

  const msUntilStart = startDate.getTime() - focus.getTime();
  let relativeTimingEn = '';
  let relativeTimingHi = '';

  if (isTimingActiveNow) {
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - focus.getTime()) / 86400000));
    relativeTimingEn = `Active now · ~${daysRemaining} days remaining`;
    relativeTimingHi = `वर्तमान में सक्रिय · ~${daysRemaining} दिन शेष`;
  } else {
    const daysUntil = Math.max(1, Math.ceil(msUntilStart / 86400000));
    relativeTimingEn = `Starts in ~${daysUntil} days`;
    relativeTimingHi = `~${daysUntil} दिनों में प्रारंभ`;
  }

  return {
    startDate: startStr,
    endDate: endStr,
    peakDate: peakStr,
    isoStartDate: startDate.toISOString(),
    isoEndDate: endDate.toISOString(),
    durationLabelEn,
    durationLabelHi,
    relativeTimingEn,
    relativeTimingHi,
    isTimingActiveNow,
    explanationEn: `Calculated from Parashari Vimshottari activation of House ${targetHouse} and operating planetary transits.`,
    explanationHi: `भाव ${targetHouse} के विंशोत्तरी सक्रियण एवं गोचर संचरण के आधार पर गणित समय अवधि।`
  };
}
